/**
 * Offline Essentials — IndexedDB snapshot cache + mutation queue.
 *
 * Uses a single idb-keyval store with namespaced keys to avoid the lazy
 * store-creation issue where the first accessed store becomes the only one
 * idb-keyval's default createStore will register.
 *
 *   snap:<url>   → { data, syncedAt }
 *   mut:<id>     → { id, url, method, body, createdAt }
 */
import { get, set, del, keys } from "idb-keyval";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const SNAP = "snap:";
const MUT = "mut:";
const DL = "dl:lesson:"; // explicit "Download for Offline" — survives cache clears.

export async function saveSnapshot(key, data) {
  try {
    await set(SNAP + key, { data, syncedAt: new Date().toISOString() });
  } catch { /* storage disabled / quota — silent degrade */ }
}

export async function getSnapshot(key) {
  try {
    return (await get(SNAP + key)) || null;
  } catch {
    return null;
  }
}

export async function clearSnapshots() {
  try {
    const all = await keys();
    for (const k of all) {
      if (typeof k === "string" && k.startsWith(SNAP)) await del(k);
    }
  } catch { /* ignore */ }
}

export async function enqueueMutation(op) {
  const id = op.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const doc = { ...op, id, createdAt: new Date().toISOString() };
  try {
    await set(MUT + id, doc);
  } catch { /* ignore */ }
  return id;
}

export async function getQueueSize() {
  try {
    const all = await keys();
    return all.filter((k) => typeof k === "string" && k.startsWith(MUT)).length;
  } catch {
    return 0;
  }
}

export async function drainQueue() {
  let replayed = 0;
  let failed = 0;
  let ids = [];
  try {
    const all = await keys();
    ids = all.filter((k) => typeof k === "string" && k.startsWith(MUT));
  } catch { return { replayed: 0, failed: 0 }; }

  for (const key of ids) {
    const op = await get(key);
    if (!op) continue;
    try {
      const fullUrl = op.url.startsWith("http") ? op.url : `${API}${op.url}`;
      await axios({
        url: fullUrl,
        method: op.method || "POST",
        data: op.body || {},
        withCredentials: true,
      });
      await del(key);
      replayed += 1;
    } catch {
      failed += 1;
    }
  }
  return { replayed, failed };
}

// ============== EXPLICIT LESSON DOWNLOADS ==============
// "Download for Offline" — an intentional, user-visible save that:
//   • Fetches the lesson JSON aggressively
//   • Fetches and stores every image referenced by visual_content[]
//   • Fetches the audio file if audio_url is present
//   • Records the user's progress snapshot
//   • Persists a metadata record `dl:lesson:{id}` so it's listable + removable

async function fetchAsBlob(url) {
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return null;
    const blob = await res.blob();
    // Convert to data URL — small lessons; keeps retrieval trivial & offline-safe.
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function downloadLessonForOffline(lessonId, { onProgress } = {}) {
  const report = (label) => onProgress && onProgress(label);
  report("fetching-lesson");

  const { data: lesson } = await axios.get(`${API}/api/lessons/${lessonId}`, { withCredentials: true });

  // Also cache a snapshot keyed the way the axios interceptor uses, so everything
  // the lesson page normally fetches is still served from IDB when offline.
  await saveSnapshot(`/api/lessons/${lessonId}`, lesson);

  // Images — fetch each, convert to data URL, store inline with the record.
  const images = [];
  const sources = Array.isArray(lesson.visual_content) ? lesson.visual_content : [];
  for (let i = 0; i < sources.length; i += 1) {
    report(`fetching-image-${i + 1}-of-${sources.length}`);
    const url = sources[i];
    const dataUrl = await fetchAsBlob(url);
    images.push({ url, dataUrl: dataUrl || url });
  }

  // Audio — same approach.
  let audio = null;
  if (lesson.audio_url) {
    report("fetching-audio");
    const dataUrl = await fetchAsBlob(lesson.audio_url);
    audio = { url: lesson.audio_url, dataUrl: dataUrl || lesson.audio_url };
  }

  // Progress snapshot for this lesson.
  let progressEntry = null;
  try {
    const { data: progress } = await axios.get(`${API}/api/progress`, { withCredentials: true });
    progressEntry = progress.find((p) => p.lesson_id === lessonId) || null;
  } catch { /* ignore */ }

  const record = {
    lessonId,
    lesson,
    images,
    audio,
    progress: progressEntry,
    downloadedAt: new Date().toISOString(),
  };
  try {
    await set(DL + lessonId, record);
  } catch (e) {
    // Likely quota exceeded — surface so the UI can message it.
    throw e;
  }
  report("done");
  return record;
}

export async function getLessonDownload(lessonId) {
  try {
    return (await get(DL + lessonId)) || null;
  } catch {
    return null;
  }
}

export async function removeLessonDownload(lessonId) {
  try {
    await del(DL + lessonId);
  } catch { /* ignore */ }
}

export async function listLessonDownloads() {
  try {
    const all = await keys();
    return all.filter((k) => typeof k === "string" && k.startsWith(DL))
              .map((k) => k.substring(DL.length));
  } catch {
    return [];
  }
}

/** Return every downloaded lesson as `{lessonId, lesson, downloadedAt, lastOpenedAt}`
 *  sorted by lastOpenedAt (or downloadedAt as fallback), newest first. */
export async function getAllLessonDownloads() {
  try {
    const all = await keys();
    const records = [];
    for (const k of all) {
      if (typeof k !== "string" || !k.startsWith(DL)) continue;
      const rec = await get(k);
      if (rec) {
        records.push({
          lessonId: rec.lessonId,
          lesson: rec.lesson,
          downloadedAt: rec.downloadedAt,
          lastOpenedAt: rec.lastOpenedAt || rec.downloadedAt,
        });
      }
    }
    return records.sort((a, b) =>
      new Date(b.lastOpenedAt).getTime() - new Date(a.lastOpenedAt).getTime()
    );
  } catch {
    return [];
  }
}

/** Update the lastOpenedAt timestamp for a downloaded lesson (sort order hint). */
export async function touchLessonDownload(lessonId) {
  try {
    const rec = await get(DL + lessonId);
    if (!rec) return;
    await set(DL + lessonId, { ...rec, lastOpenedAt: new Date().toISOString() });
  } catch { /* ignore */ }
}
